/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { faCopy, faEdit, faPlus, faTrash } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { useSelector } from "react-redux";
import {
    Alert,
    Badge,
    Button,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Table,
} from "reactstrap";
import { CONFIG_TYPES } from "../../../../actions/Tenants/config/constants";
import { FruitConfig } from "../../../../actions/Tenants/config/constantsTyped";
import { isStorageDefinition, PROGRAM_KIND_LABELS, programKindOf } from "../../../../constants/ripeningStorePrograms";
import {
    GatewayRipeningHealth,
    HealthHop,
    RipeningProgram,
    RipeningProgramInput,
    useGatewayRipeningHealth,
    useGatewayRipeningScope,
    useRipeningPrograms,
} from "../../../../hooks/useRipeningPrograms";
import { useTenantActiveConfigs } from "../../../../hooks/useTenantConfigQueries";
import { Loading } from "../../../Helper/Loading";
import RipeningProgramForm, {
    cloneRipeningProgram,
    defaultRipeningProgram,
    normalizeRipeningProgram,
    validateRipeningProgram,
} from "./RipeningProgramForm";

interface RootState {
    devices: {
        current?: {
            UID?: string;
            tenant_id?: string;
        };
    };
}

interface HealthRequestError {
    response?: {
        data?: GatewayRipeningHealth;
    };
}

const healthLabels: Record<keyof GatewayRipeningHealth["hops"], string> = {
    service: "Service",
    broker: "MQTT broker",
    gateway: "Gateway",
    reliance: "Reliance",
};

function HealthBadge({ label, hop }: { label: string; hop?: HealthHop }) {
    return (
        <div className="d-flex align-items-center gap-2">
            <Badge color={hop?.ok ? "success" : "danger"}>{hop?.ok ? "Online" : "Unavailable"}</Badge>
            <span className="fw-semibold">{label}</span>
            {hop?.detail && <span className="text-muted small">{hop.detail}</span>}
        </div>
    );
}

// The program service has no error code for this; its Reliance hop detail is the only signal,
// e.g. "gateway is not registered (PUT /gateways/{id})".
function isGatewayUnregistered(health?: GatewayRipeningHealth) {
    return /not registered/i.test(health?.hops.reliance?.detail ?? "");
}

function durationLabel(minutes: number) {
    const days = minutes / (24 * 60);
    if (Number.isInteger(days)) return `${days} day${days === 1 ? "" : "s"}`;
    const hours = minutes / 60;
    if (Number.isInteger(hours)) return `${hours} hour${hours === 1 ? "" : "s"}`;
    return `${minutes} minutes`;
}

// The program service answers {"error": ...}; the AVOS proxy's own failures answer {"detail": ...}.
function serviceErrorMessage(error: unknown): string | undefined {
    const data = (error as { response?: { data?: { error?: unknown; detail?: unknown } } })?.response?.data;
    const message = data?.error ?? data?.detail;
    return typeof message === "string" ? message : undefined;
}

// * Wide enough for all 7 phases on a big screen; the phase grid still scrolls sideways below that.
const programModalCss = css`
    &.modal-dialog { max-width: min(1400px, 95vw); }
`;

function saveButtonLabel(isSaving: boolean, editing: boolean) {
    if (isSaving) return "Saving...";
    return editing ? "Update Program" : "Create Program";
}

export default function RipeningProgramsTab() {
    const device = useSelector((state: RootState) => state.devices.current);
    const tenantConfigsQuery = useTenantActiveConfigs(device?.tenant_id);
    const gatewayId = device?.UID;
    const fruitTypes = (tenantConfigsQuery.data ?? [])
        .filter((config) => config.type === CONFIG_TYPES.FRUIT_TYPE)
        .map((config) => config.config as FruitConfig);
    // The tenant comes from the backend's own records for this gateway (and is re-verified on
    // every program service request).
    const scopeQuery = useGatewayRipeningScope(gatewayId);
    const serviceScope = { tenantId: scopeQuery.data?.tenant_id };
    const programsQuery = useRipeningPrograms(gatewayId, serviceScope);
    const healthQuery = useGatewayRipeningHealth(gatewayId, serviceScope);
    const [modalOpen, setModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [editingProgram, setEditingProgram] = useState<RipeningProgram | null>(null);
    const [programToDelete, setProgramToDelete] = useState<RipeningProgram | null>(null);
    const [formData, setFormData] = useState<RipeningProgramInput>(defaultRipeningProgram());
    const [formError, setFormError] = useState<string | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);

    if (scopeQuery.isLoading || programsQuery.isLoading) return <Loading />;
    if (scopeQuery.isError) {
        return <Alert color="danger">Could not determine the tenant of this gateway.</Alert>;
    }

    const programs = programsQuery.data?.results ?? [];
    const healthError = healthQuery.error as HealthRequestError | null;
    // A 503 from the program service still carries a health report; a 503 from our own backend
    // (e.g. service URL not configured) is just `{ detail }` and must not be read as one.
    const errorHealth = healthError?.response?.data;
    const health = healthQuery.data ?? (errorHealth?.hops ? errorHealth : undefined);
    const isSaving = programsQuery.createMutation.isPending || programsQuery.updateMutation.isPending;

    const closeModal = () => {
        setModalOpen(false);
        setEditingProgram(null);
        setFormData(defaultRipeningProgram());
        setFormError(null);
    };

    const openCreateModal = () => {
        setActionError(null);
        setEditingProgram(null);
        setFormData(defaultRipeningProgram());
        setFormError(null);
        setModalOpen(true);
    };

    // * A copy opens as a new program: nothing is saved until Create.
    const openDuplicateModal = (program: RipeningProgram) => {
        setActionError(null);
        setEditingProgram(null);
        setFormData({ ...cloneRipeningProgram(program), name: `${program.name} (copy)` });
        setFormError(null);
        setModalOpen(true);
    };

    const openEditModal = (program: RipeningProgram) => {
        setActionError(null);
        setEditingProgram(program);
        setFormData(cloneRipeningProgram(program));
        setFormError(null);
        setModalOpen(true);
    };

    const handleSubmit = async () => {
        const program = normalizeRipeningProgram(formData);
        const error = validateRipeningProgram(program);
        if (error) {
            setFormError(error);
            return;
        }
        try {
            if (editingProgram) {
                await programsQuery.updateMutation.mutateAsync({
                    programId: editingProgram.id,
                    program,
                });
            } else {
                await programsQuery.createMutation.mutateAsync(program);
            }
            closeModal();
        } catch (error) {
            const reason = serviceErrorMessage(error);
            setFormError(reason ? `The ripening program could not be saved: ${reason}` : "The ripening program could not be saved.");
        }
    };

    const handleDelete = async () => {
        if (!programToDelete) return;
        try {
            await programsQuery.deleteMutation.mutateAsync(programToDelete.id);
            setActionError(null);
        } catch {
            setActionError("The ripening program could not be deleted.");
        } finally {
            setDeleteModalOpen(false);
            setProgramToDelete(null);
        }
    };

    const renderHealth = () => {
        if (healthQuery.isError && !health) {
            return (
                <Alert color="danger" className="mb-0">
                    The ripening program service could not complete the end-to-end connection check.
                </Alert>
            );
        }
        if (healthQuery.isLoading) {
            return <div className="text-muted border rounded p-3">Checking service and Reliance connectivity...</div>;
        }
        return (
            <>
                {!health?.ok && (
                    <Alert color="warning">
                        {isGatewayUnregistered(health)
                            ? "This gateway isn't registered with the ripening program service yet, so it can't "
                              + "reach Reliance. Ask the ripening program service team to register it."
                            : "One or more connections in the path to Reliance are unavailable."}
                    </Alert>
                )}
                <div className="d-flex flex-wrap gap-3 border rounded p-3 bg-light">
                    {(Object.keys(healthLabels) as Array<keyof GatewayRipeningHealth["hops"]>).map((key) => (
                        <HealthBadge key={key} label={healthLabels[key]} hop={health?.hops[key]} />
                    ))}
                </div>
            </>
        );
    };

    const renderPrograms = () => {
        if (programsQuery.isError) return <Alert color="danger">Failed to load ripening programs.</Alert>;
        if (programs.length === 0) {
            return <div className="text-muted py-4">No ripening programs configured for this gateway.</div>;
        }
        return (
            <Table hover responsive>
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Fruit type</th>
                        <th>Mode</th>
                        <th>Duration</th>
                        <th>Status</th>
                        <th className="text-end">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {programs.map((program) => (
                        <tr key={program.id}>
                            <td>{program.name}</td>
                            <td>{program.fruit_type}</td>
                            <td>{PROGRAM_KIND_LABELS[programKindOf(program.definition)]}</td>
                            <td>{isStorageDefinition(program.definition) ? "Until stopped" : durationLabel(program.duration_minutes)}</td>
                            <td>
                                <Badge color={program.active ? "success" : "secondary"}>
                                    {program.active ? "Active" : "Inactive"}
                                </Badge>
                            </td>
                            <td className="text-end text-nowrap">
                                <Button size="sm" color="secondary" className="me-1" onClick={() => openEditModal(program)} aria-label={`Edit ${program.name}`} title="Edit">
                                    <FontAwesomeIcon icon={faEdit} />
                                </Button>
                                <Button size="sm" color="light" className="me-1" onClick={() => openDuplicateModal(program)} aria-label={`Duplicate ${program.name}`} title="Duplicate">
                                    <FontAwesomeIcon icon={faCopy} className="me-1" />
                                    Duplicate
                                </Button>
                                <Button
                                    size="sm"
                                    color="danger"
                                    onClick={() => {
                                        setProgramToDelete(program);
                                        setDeleteModalOpen(true);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        );
    };

    return (
        <div className="d-flex flex-column gap-4">
            <section>
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h4 className="mb-0">Connection status</h4>
                    {health?.checked_at && (
                        <span className="text-muted small">
                            Checked {new Date(health.checked_at).toLocaleString()}
                        </span>
                    )}
                </div>
                {renderHealth()}
            </section>

            <section>
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h4 className="mb-0">Ripening programs</h4>
                    <Button color="primary" onClick={openCreateModal}>
                        <FontAwesomeIcon icon={faPlus} className="me-1" />
                        Add Program
                    </Button>
                </div>
                {actionError && <Alert color="danger">{actionError}</Alert>}
                {renderPrograms()}
            </section>

            <Modal isOpen={modalOpen} toggle={closeModal} size="xl" css={programModalCss}>
                <ModalHeader toggle={closeModal}>
                    {editingProgram ? "Edit Ripening Program" : "Add Ripening Program"}
                </ModalHeader>
                <ModalBody className="px-4 py-3">
                    {formError && <Alert color="danger">{formError}</Alert>}
                    <RipeningProgramForm
                        value={formData}
                        fruitTypes={fruitTypes}
                        onChange={setFormData}
                    />
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={closeModal} disabled={isSaving}>Cancel</Button>
                    <Button color="primary" onClick={handleSubmit} disabled={isSaving}>
                        {saveButtonLabel(isSaving, !!editingProgram)}
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={deleteModalOpen} toggle={() => setDeleteModalOpen(false)}>
                <ModalHeader toggle={() => setDeleteModalOpen(false)}>Delete Ripening Program</ModalHeader>
                <ModalBody>
                    Delete <strong>{programToDelete?.name}</strong>? Existing cycles keep their stored program snapshot.
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
                    <Button color="danger" onClick={handleDelete} disabled={programsQuery.deleteMutation.isPending}>
                        {programsQuery.deleteMutation.isPending ? "Deleting..." : "Delete Program"}
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
}
